"use client";

import React, {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from "react";
import clsx from "clsx";
import Image from "next/image";

import CTAButton from "@/components/atoms/buttons/CTAButton";
import HtmlText from "../common/HtmlText";

import { ITextButton } from "@/components/atoms/buttons/types";

// -------------------------------------------------------------------------------------------------

export type IHeroSliderSectionContent = {
  heading?: string;
  animatedHeadings?: string[];
  description?: ReactNode[];
  buttons?: (ITextButton | undefined)[];
  /** Persistent secondary CTA shown next to the cycling primary one.
   *  Optional — falls back to a "Schedule a Meet" link when omitted. */
  secondaryButton?: ITextButton;
  background?: {
    image?: string;
    color?: string;
  }[];
  isAssessmentAvailable?: boolean;
};

// Sensible default for the secondary CTA when Strapi hasn't supplied one yet.
// Once `secondary_button` is added to the hero-slider-section schema and
// populated, that value overrides this default.
const DEFAULT_SECONDARY_BUTTON: ITextButton = {
  id: "hero-secondary-cta-schedule",
  text: "Book a Discovery Call",
  link: "/schedule",
};

export type IHeroSliderSectionProps = HTMLAttributes<HTMLElement> &
  IHeroSliderSectionContent;

const HeroSliderSection: FC<IHeroSliderSectionProps> = (props) => {
  const {
    heading,
    animatedHeadings,
    description,
    buttons,
    secondaryButton = DEFAULT_SECONDARY_BUTTON,
    background,
    isAssessmentAvailable = true,
    className,
    ...other
  } = props;

  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCounter(counter + 1);
    }, 3000);
    return () => {
      clearTimeout(timeout);
    };
  }, [counter]);

  const slideCount = background?.length ?? 0;
  const activeIdx = slideCount ? counter % slideCount : 0;
  const currentBg = slideCount ? background![activeIdx] : undefined;

  // Soft floating glow palette — one accent pair per slide. mix-blend-screen
  // lightens whatever bg color is behind, so these colors are read as a
  // tinted glow on top of the slide's dark base rather than as solid orbs.
  // Order matches the Strapi slide order (ATTACK / DEFEND / COMPLY / PRIVACY).
  const GLOW_PRESETS = [
    { a: "rgba(56, 189, 248, 0.55)", b: "rgba(99, 102, 241, 0.45)" }, // ATTACK   — cyan + indigo
    { a: "rgba(96, 165, 250, 0.55)", b: "rgba(45, 212, 191, 0.45)" }, // DEFEND   — blue + teal
    { a: "rgba(167, 139, 250, 0.55)", b: "rgba(244, 114, 182, 0.45)" }, // COMPLY   — violet + pink
    { a: "rgba(52, 211, 153, 0.55)", b: "rgba(56, 189, 248, 0.40)" }, // PRIVACY  — emerald + cyan
  ];
  const glow = GLOW_PRESETS[activeIdx % GLOW_PRESETS.length];

  return (
    // -mt-[60px] pulls the section up so its TOP aligns with the
    // viewport top (under the transparent header). Panel height is
    // 100vh + 60px so that after the 60px pull-up the panel covers
    // the FULL viewport (viewport top to viewport bottom). The hero
    // BG color is therefore visible THROUGH the transparent header
    // at the top of the page — header has no bg of its own, the
    // hero color fills behind it.
    <section className={clsx("-mt-[60px]", className)} {...other}>
      <div>
        {/* start: Attack banner section*/}
        <div
          className="relative flex min-h-[calc(100vh+60px)] w-full items-center justify-center overflow-hidden bg-white bg-blend-overlay transition-[background-color] duration-1000 ease-in-out"
          style={{
            backgroundColor: currentBg?.color || "#0A0E1A",
          }}
        >
          {/* Soft floating glow — two blurred orbs that drift slowly across
              the hero, mix-blend-screen so they tint the dark base color
              instead of sitting on top of it. The glow palette rotates with
              the slide (cyan/indigo for ATTACK, blue/teal for DEFEND, etc.)
              so each slide still feels visually distinct without bright
              alarm-bell colors. transition-[background] crossfades the tint
              when slides rotate. pointer-events-none so the orbs never
              block clicks on the hero CTAs. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-[15%] top-[10%] h-[55vw] max-h-[700px] w-[55vw] max-w-[700px] animate-float-a rounded-full opacity-80 mix-blend-screen blur-3xl transition-[background] duration-1000 ease-in-out"
            style={{
              background: `radial-gradient(closest-side, ${glow.a}, transparent 70%)`,
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-[10%] bottom-[5%] h-[50vw] max-h-[640px] w-[50vw] max-w-[640px] animate-float-b rounded-full opacity-75 mix-blend-screen blur-3xl transition-[background] duration-1000 ease-in-out"
            style={{
              background: `radial-gradient(closest-side, ${glow.b}, transparent 70%)`,
            }}
          />

          {/* Background image — rendered as <Image priority> so the browser
              preloads it via <link rel="preload" as="image">, instead of
              waiting for JS to apply a CSS background-image. This is the LCP
              element on the homepage; priority only on the first slide so
              subsequent slides lazy-load on rotation. */}
          {slideCount > 0 &&
            background!.map((bg, idx) =>
              bg.image ? (
                <Image
                  key={`${idx}-${bg.image}`}
                  src={bg.image}
                  alt=""
                  fill
                  priority={idx === 0}
                  fetchPriority={idx === 0 ? "high" : "auto"}
                  loading={idx === 0 ? "eager" : "lazy"}
                  sizes="100vw"
                  quality={75}
                  className={clsx(
                    "absolute inset-0 object-cover transition-opacity duration-1000",
                    counter % slideCount === idx ? "opacity-100" : "opacity-0",
                  )}
                />
              ) : null,
            )}

          <div className="relative z-10 flex h-full min-h-0 w-full items-center justify-center gap-8 px-6 sm:px-12 lg:px-24 xl:px-32 2xl:px-[120px]">
            {/* Center content - full width */}
            <div className="flex w-full flex-col items-center justify-center gap-4 sm:gap-8">
              {/* Heading at top */}
              {heading && (
                <h1 className="max-w-2xl bg-gradient-to-b from-white via-white/90 to-white/60 bg-clip-text text-center font-space-grotesk text-3xl font-semibold !leading-tight text-transparent [text-wrap:balance] sm:text-3xl lg:text-4xl xl:text-5xl">
                  {heading}
                </h1>
              )}

              {/* Animated Title — fluid clamp() sizing. Tightened from 21vw →
                  19vw with cap 200 because Bricolage bold letter-widths are
                  ~0.6 of font-size, not the conservative 0.55 we estimated;
                  the previous 21vw clipped 'PRIVACY.' by a few pixels on
                  iPhone 16 Pro Max. Now leaves ~5% safety margin on every
                  viewport. Container = text size for seamless slide. */}
              {animatedHeadings && animatedHeadings.length > 0 && (
                <div className="flex h-[clamp(60px,19vw,200px)] w-full flex-col overflow-hidden text-center">
                  {animatedHeadings.map((name, index) => (
                    <span
                      key={index}
                      className={clsx(
                        "flex h-full w-full items-center justify-center break-words px-4 font-bricolage-grotesque text-[clamp(60px,19vw,200px)] font-semibold !leading-none text-white transition-transform duration-1000 ease-in-out",
                      )}
                      style={{
                        transform: `translateY(-${(counter % animatedHeadings.length) * 100}%)`,
                      }}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              {description &&
                description.length > 0 &&
                description[counter % description.length] && (
                  <div className="flex max-w-3xl text-center font-bricolage-grotesque text-base font-normal text-white lg:text-lg">
                    <HtmlText className="prose-headings:text-white prose-p:text-white prose-strong:text-white">
                      {description[counter % description.length]}
                    </HtmlText>
                  </div>
                )}

              {/* Hero CTAs — applying the site-wide 3-tier rule:
                    orange (primary, rotating per slide from Strapi) →
                    primary blue (secondary, persistent "Schedule a Meet").
                  size="large" because hero uses the larger CTA scale. */}
              {((buttons &&
                buttons.length > 0 &&
                buttons[counter % buttons.length]) ||
                secondaryButton) && (
                <div className="flex flex-wrap items-center justify-center gap-4 px-4 sm:gap-6">
                  {buttons &&
                    buttons.length > 0 &&
                    buttons[counter % buttons.length] && (
                      <CTAButton
                        id={buttons[counter % buttons.length]?.id}
                        href={buttons[counter % buttons.length]?.link}
                        target={buttons[counter % buttons.length]?.target}
                        variant="orange"
                        radius="full"
                        size="large"
                        showIcon
                        iconType="arrow"
                        iconPosition="right"
                        className="w-fit"
                      >
                        {buttons[counter % buttons.length]?.text}
                      </CTAButton>
                    )}

                  {secondaryButton && (
                    <CTAButton
                      id={secondaryButton.id}
                      href={secondaryButton.link}
                      target={secondaryButton.target}
                      variant="primary"
                      radius="full"
                      size="large"
                      showIcon
                      iconType="arrow"
                      iconPosition="right"
                      className="w-fit"
                    >
                      {secondaryButton.text}
                    </CTAButton>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* End: Attack banner section */}
      </div>
    </section>
  );
};

export default HeroSliderSection;
